<template>
  <div
    class="clear-time-form"
    :class="{
      'edit-mode': form.isEditMode
    }"
  >
    <div class="d-flex gap-2 align-items-center flex-wrap">
      <h2 class="queue-header-text" id="clearTimeFormHeading">
        {{ form.isEditMode ? "Editing Clear Time" : "Add Clear Time" }}
      </h2>
      <hr class="queue-header-horizontal-line flex-grow-1 align-self-center" />
    </div>

    <form class="d-flex flex-column gap-4 h-100">
      <!-- LABEL FOR TIME -->
      <div class="d-flex flex-column gap-2">
        <Label required>Label</Label>
        <InputField
          id="label"
          placeholder="Enter label for selected time"
          v-model="form.label"
          :invalid-feedback="errors.label"
        />
        <span id="labelError" class="sr-only">{{ errors.label }}</span>
        <!-- LABEL FOR TIME -->
      </div>

      <!-- QUEUE CLEAR TIME -->
      <div class="d-flex flex-column gap-2">
        <Label required>Queue clear time</Label>
        <CustomTimePicker
          id="queueClearTime"
          format="HH:mm"
          mode="time"
          v-model="form.queueClearTime"
          placeholder="Enter Clear Time"
          :invalid-feedback="errors.queueClearTime"
        />
        <span id="queueClearTimeError" class="sr-only">
          {{ errors.queueClearTime }}
        </span>
      </div>
      <!-- QUEUE CLEAR TIME -->

      <!-- RECURRENCE SECTION -->
      <div class="d-flex flex-column gap-2">
        <Label required>Recurrence</Label>
        <div class="d-flex flex-wrap gap-2">
          <CustomCheck
            v-for="item in daysOfWeek"
            :key="`recurrence-day-${item.id}`"
            :id="`recurrence-day-${item.id}`"
            :label="item.name"
            v-model="form.recurrenceDays[item.id]"
          />
        </div>
        <div v-if="errors.recurrenceDays" class="invalid-message text-start">
          {{ errors.recurrenceDays }}
        </div>
        <span id="recurrenceDaysError" class="sr-only">
          {{ errors.recurrenceDays }}
        </span>
      </div>
      <!-- RECURRENCE SECTION -->

      <!-- ERROR HANDLER -->
      <ErrorHandler
        :error="serverError"
        keep-alive
        @done="serverError = null"
      />
      <!-- ERROR HANDLER -->

      <div class="flex-grow-1"></div>

      <!-- FORM ACTIONS -->
      <div class="d-flex flex-wrap gap-4">
        <!-- EDIT MODE ACTIONS -->
        <template v-if="form.isEditMode">
          <LoadingButton rounded type="button" @click="onCancel">
            Cancel
          </LoadingButton>

          <LoadingButton
            :is-loading="isLoading"
            rounded
            solid
            type="submit"
            @click="
              ($event) => {
                $event.preventDefault()
                return onEdit()
              }
            "
          >
            Save Changes
          </LoadingButton>
        </template>
        <!-- EDIT MODE ACTIONS -->

        <!-- ADD MODE ACTIONS -->
        <template v-else>
          <LoadingButton
            :is-loading="isLoading"
            rounded
            solid
            type="submit"
            @click="
              ($event) => {
                $event.preventDefault()
                return onAdd()
              }
            "
          >
            Add to List
          </LoadingButton>
        </template>
        <!-- ADD MODE ACTIONS -->
      </div>
      <!-- FORM ACTIONS -->
    </form>
  </div>
</template>

<script>
import queueConstants from "@/constants/queueConstants"
import passHelpers from "@/helpers/index"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import CustomCheck from "@/v3components/shared/Form/CustomCheck.vue"
import CustomTimePicker from "@/v3components/shared/Form/CustomTimePicker.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import Label from "@/v3components/shared/Form/Label.vue"
import ErrorHandler from "@/v3components/VisitorErrorHandler.vue"
import { useVuelidate } from "@vuelidate/core"
import { helpers, required } from "@vuelidate/validators"
import { computed, reactive, ref, watch } from "vue"
import { useStore } from "vuex"

const atLeastOneDay = (value) => {
  return Object.values(value).some((day) => day === true)
}

export default {
  name: "AddClearTimeForm",
  components: {
    InputField,
    Label,
    LoadingButton,
    CustomTimePicker,
    CustomCheck,
    ErrorHandler
  },
  setup() {
    const store = useStore()

    const serverError = ref(null)
    const isLoading = ref(false)

    const daysOfWeek = [
      { id: 1, name: "Mon", day: "Monday" },
      { id: 2, name: "Tue", day: "Tuesday" },
      { id: 3, name: "Wed", day: "Wednesday" },
      { id: 4, name: "Thu", day: "Thursday" },
      { id: 5, name: "Fri", day: "Friday" },
      { id: 6, name: "Sat", day: "Saturday" },
      { id: 7, name: "Sun", day: "Sunday" }
    ]

    const form = reactive({
      isEditMode: false,
      id: "",
      queueClearTime: "",
      label: "",
      recurrenceDays: {
        1: false, // Monday
        2: false, // Tuesday
        3: false, // Wednesday
        4: false, // Thursday
        5: false, // Friday
        6: false, // Saturday
        7: false // Sunday
      }
    })

    const rules = {
      queueClearTime: {
        required: helpers.withMessage(
          queueConstants.FORM_VALIDATIONS.CLEAR_TIME_REQUIRED,
          required
        )
      },
      label: {
        required: helpers.withMessage(
          queueConstants.FORM_VALIDATIONS.LABEL_TEXT_REQUIRED,
          required
        )
      },
      recurrenceDays: {
        atLeastOneDay: helpers.withMessage(
          queueConstants.FORM_VALIDATIONS.AT_LEAST_ONE_DAY,
          atLeastOneDay
        )
      }
    }

    const v$ = useVuelidate(rules, form)

    const errors = computed(() => {
      if (serverError.value) {
        return {
          queueClearTime:
            serverError?.value?.response?.data?.errors?.clear_time?.join(
              ", "
            ) || "",
          label:
            serverError?.value?.response?.data?.errors?.label?.join(", ") || "",
          recurrenceDays:
            serverError?.value?.response?.data?.errors?.recurrence?.join(
              ", "
            ) || ""
        }
      }
      return {
        queueClearTime: v$?.value?.queueClearTime.$error
          ? v$?.value?.queueClearTime.$errors
              .map((error) => error.$message)
              .join(", ")
          : "",
        label: v$?.value?.label.$error
          ? v$?.value?.label.$errors.map((error) => error.$message).join(", ")
          : "",
        recurrenceDays: v$?.value?.recurrenceDays.$error
          ? v$?.value?.recurrenceDays.$errors
              .map((error) => error.$message)
              .join(", ")
          : ""
      }
    })

    const clearTimeToEdit = computed(() => {
      return store.getters["queueSettings/clearTimeToEdit"]
    })

    const resetForm = () => {
      form.isEditMode = false
      form.id = ""
      form.queueClearTime = ""
      form.label = ""
      Object.keys(form.recurrenceDays).forEach((key) => {
        form.recurrenceDays[key] = false
      })
      v$?.value?.$reset()
      serverError.value = null
    }

    const onAdd = async () => {
      const isFormCorrect = await v$.value.$validate()
      if (!isFormCorrect) {
        return
      }

      try {
        isLoading.value = true

        const data = {
          label: form.label,
          clearTime: form.queueClearTime,
          recurrence: daysOfWeek.map((day) => ({
            day: day.day,
            status: form.recurrenceDays[day.id] ? 1 : 0
          }))
        }
        await store.dispatch("queueSettings/addClearTime", data)
        store.commit("queueSettings/setUpdateClearTimeList", true)

        return resetForm()
      } catch (error) {
        serverError.value = error
      } finally {
        isLoading.value = false
      }
    }

    const onEdit = async () => {
      const isFormCorrect = await v$.value.$validate()
      if (!isFormCorrect) {
        return
      }

      try {
        isLoading.value = true

        const data = {
          id: form.id,
          clearTime: form.queueClearTime,
          label: form.label,
          recurrence: daysOfWeek.map((day) => ({
            day: day.day,
            status: form.recurrenceDays[day.id] ? 1 : 0
          }))
        }

        await store.dispatch("queueSettings/updateClearTime", data)
        store.commit("queueSettings/setUpdateClearTimeList", true)
        store.commit("queueSettings/setClearTimeToEdit", null)

        return resetForm()
      } catch (error) {
        serverError.value = error
      } finally {
        isLoading.value = false
      }
    }

    const populateFormForEdit = (valueToEdit) => {
      resetForm()
      form.isEditMode = true
      form.id = valueToEdit.id
      form.queueClearTime = passHelpers.convertUTCToLocalTime(
        valueToEdit?.clear_time,
        null,
        "HH:mm"
      )
      form.label = valueToEdit.label
      daysOfWeek.forEach((dayOfWeekItem) => {
        form.recurrenceDays[dayOfWeekItem.id] = valueToEdit?.recurrence?.some(
          (recurrence) =>
            recurrence?.day?.trim()?.toLowerCase() ===
              dayOfWeekItem?.day?.toLowerCase() && recurrence.status === 1
        )
      })
    }

    const onCancel = () => {
      resetForm()
      store.commit("queueSettings/setClearTimeToEdit", null)
    }

    watch(
      () => form.queueClearTime,
      () => {
        if (serverError?.value?.response?.data?.errors?.clear_time) {
          serverError.value = null
        }
      }
    )

    watch(
      () => form.label,
      () => {
        if (serverError?.value?.response?.data?.errors?.label) {
          serverError.value = null
        }
      }
    )

    watch(
      () => form.recurrenceDays,
      () => {
        if (serverError?.value?.response?.data?.errors?.recurrence) {
          serverError.value = null
        }
      },
      { deep: true }
    )

    watch(
      () => clearTimeToEdit.value,
      (newValue) => {
        if (newValue) {
          populateFormForEdit(newValue)
        }
      },
      { immediate: true }
    )

    return {
      isLoading,
      form,
      errors,
      daysOfWeek,
      onAdd,
      onEdit,
      onCancel,
      serverError
    }
  }
}
</script>
